import React, { useEffect, useState } from 'react'
import {
  DataGrid, useGridApiRef,
} from '@mui/x-data-grid'
import { styled } from '@mui/material/styles'

import { extractDateTime } from "../../utils/date"
import notFound from "../Contacts/Table/NotFoundData"
import EditNotesModal from "./EditNotesModal"
import { StorageGetItem, USER } from "../../lib/storage"
import ConfirmModal from "../../components/ConfirmModal/ConfirmModal"
import { removeNoteById } from "../../features/notes/notes"
import { showSnackBar } from "../../components/Snackbar/Snackbar"
import { useDispatch } from "react-redux"
import { useMyContextLoading } from "../../components/LoadingContextProvider/LoadingContextProvider"
import { formatUserWithContactGroups, SYSTEM_NOTE_AUTHOR } from "../../utils/assignees"
import { useIsSupervisor } from "../../hooks/user"
import { MENTION_PATTERN, NoteWithMentions } from "./NoteWithMentions"
import { LuPencil, LuTrash2 } from "react-icons/lu"
import ConfirmDialog from "../../components/ConfirmDialog/ConfirmDialog"
import { useUiKit } from "../../components/UiKit/UiKitContext"
import { failureOf } from "../../utils/errorHelpers"

// Local styled component removed in favor of global theme overrides

const FLICKER_GUARD_PROPS = { scrollbarSize: 0, rowPositionsDebounceMs: 0 }

const NOTE_TITLE_LIMIT = 40

const noteDeleteTitle = (text) => {
  const preview = (text || "").replace(MENTION_PATTERN, "@$1").replace(/\s+/g, " ").trim()
  if (!preview) return "Delete this note?"
  const shown = preview.length > NOTE_TITLE_LIMIT ? `${preview.slice(0, NOTE_TITLE_LIMIT).trimEnd()}…` : preview
  return `Delete the note "${shown}"?`
}

const TableList = ({ triggerUpdate, curUserId, notes, flickerGuard }) => {
  const apiRef = useGridApiRef()
  const dispatch = useDispatch()
  const isSupervisor = useIsSupervisor()
  const { triggerLoading } = useMyContextLoading()
  const { confirm: kitConfirm } = useUiKit()
  const [openedNoteId, setOpenedNoteId] = useState(null)
  const [userRole, setUserRole] = useState("")
  const [openDeleteTaskModal, setOpenDeleteTaskModal] = useState("")

  useEffect(() => {
    StorageGetItem(USER).then((result) => {
      setUserRole(result?.role)
    })
  }, [])

  const handleOpenModal = (noteId) => {
    setOpenedNoteId(noteId)
  }

  const handleCloseModal = () => {
    setOpenedNoteId(null)
  }

  const deleteNote = async (noteId) => {
    triggerLoading(true)
    try {
      const response = await dispatch(removeNoteById(noteId))
      if (response?.payload?.data?._id) {
        triggerUpdate(prev => !prev)
        showSnackBar("success", "Note successfully deleted")
      } else {
        showSnackBar("error", "Something went wrong, please try again")
      }
    } catch (error) {
      console.error(error)
    }
    triggerLoading(false)
  }

  const deleteNoteOrThrow = async (noteId) => {
    const response = await dispatch(removeNoteById(noteId))
    if (!response?.payload?.data?._id) {
      throw failureOf(response, "Something went wrong, please try again")
    }
    triggerUpdate(prev => !prev)
    showSnackBar("success", "Note successfully deleted")
  }

  const columns = [
    {
      field: "id",
      headerName: "ID",
      width: 50,
    },
    {
      field: "createdBy",
      renderCell: (params) => (
        <div>
          <div className="font-bold whitespace-normal" key={params?.row?.createdBy?.id}>
            {/* A note with no `createdBy` is authored by the system, not by a missing person.
                The backend writes `creatorId: null` DELIBERATELY for the payment "submitted for
                processing" note (services/payments/payments.js — the operator id is not threaded
                into that call, and `createdBy` is optional on the note schema). Without this
                branch those notes rendered the developer placeholder "!! creator not found",
                which is client-visible and reads like a data fault.
                Mirrors the same pattern in containers/History/Table.jsx. */}
            {params?.row?.createdBy
              ? formatUserWithContactGroups({ user: params?.row?.createdBy, userRole })
              : SYSTEM_NOTE_AUTHOR}
          </div>
          <div className="flex flex-col">
            <span>{`${extractDateTime(params?.row?.createdAt)}`}</span>
            {params?.row?.createdAt !== params?.row?.updatedAt &&
              <span className="text-[12px] text-text-secondary">
                <span className="mr-1 font-bold">Edited:</span>
                <span>{extractDateTime(params?.row?.updatedAt)}</span>
              </span>
            }
          </div>
        </div>
      ),
      headerName: "User",
      flex: 1,
      sortable: false,
    },
    {
      field: "note",
      headerName: "Note",
      renderCell: (params) => (
        <NoteWithMentions
          text={params?.row?.text}
          mentions={params?.row?.mentions}
        />
      ),
      flex: 1,
      sortable: false,
    }
  ]

  const permittedRoles = userRole === 'customer' || isSupervisor
  permittedRoles && columns.push({
    field: "action",
    headerName: "Action",
    renderCell: (params) => {
      const noteId = params?.row?._id
      const removeFunc = () => deleteNote(params?.row?._id)
      return (
        <div key={noteId} className="flex justify-between w-full pr-[6px]">
          <button onClick={() => handleOpenModal(noteId)}>
            <LuPencil size={18} className="text-text-secondary" />
          </button>
          {openedNoteId === noteId && (
            <EditNotesModal
              triggerUpdate={triggerUpdate}
              curUserId={curUserId}
              customOpenModal={true}
              onClose={handleCloseModal}
              dataRow={params?.row}
            />
          )}

          <div className="cursor-pointer" onClick={() => setOpenDeleteTaskModal(params?.row?._id)}>
            <LuTrash2 size={18} className="text-text-secondary" />
          </div>
          {openDeleteTaskModal === params?.row?._id && (kitConfirm ?
            <ConfirmDialog
              open={Boolean(openDeleteTaskModal)}
              onClose={() => setOpenDeleteTaskModal("")}
              title={noteDeleteTitle(params?.row?.text)}
              impact={["The note is removed from this contact's notes.", "This cannot be undone."]}
              confirmLabel="Delete note"
              tone="danger"
              onConfirm={() => deleteNoteOrThrow(params?.row?._id)}
            /> :
            <ConfirmModal leftTopIcon={<LuTrash2 size={18} className="text-text-secondary" />}
              title="Are you sure to delete this Note?"
              modalButtonTitle={"Delete"} classes=""
              open={openDeleteTaskModal}
              setOpen={setOpenDeleteTaskModal}
              functionButton={removeFunc}
            >
              You will not be able to restore it later
            </ConfirmModal>)}
        </div>
      )
    },
    sortable: false,
    width: 80,
  })

  return (
    <div>
      <DataGrid
        sx={{
          border: 'none',
          '& .MuiDataGrid-columnHeaders': {
            '.MuiButtonBase-root[aria-haspopup="menu"]': {
              display: 'none'
            }
          },
          ".MuiDataGrid-virtualScroller": {
            minHeight: notes?.length === 0 ? 300 : "",
            ...(flickerGuard && { overflowY: 'hidden' }),
          },
          "& .MuiDataGrid-cell": {
            whiteSpace: "normal",
            alignItems: "flex-start",
            display: "flex",
          },
          "& .MuiDataGrid-row": {
            alignItems: "flex-start",
            paddingTop: '10px',
            paddingBottom: '10px'
          },
        }}
        getRowHeight={() => 'auto'}
        {...(flickerGuard ? FLICKER_GUARD_PROPS : {})}
        columns={columns}
        slots={{
          noRowsOverlay: notFound,
        }}
        columnVisibilityModel={{
          id: false,
        }}
        rows={notes}
        apiRef={apiRef}
        getRowId={(row) => row._id}
        hideFooterPagination={true}
        checkboxSelection={false}
        disableRowSelectionOnClick
        disableColumnSelector
        getRowClassName={(params) =>
          params?.row?.status === "Failed" ? "bg-error-200" : ""
        }
      />
    </div>
  )
}

export default TableList